import React, { useState, useCallback, useMemo, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { isEmpty } from "lodash";
import NotesDialog from "./NotesDialog";

const filter = createFilterOptions({
  trim: true,
  matchFrom: "start",
});

export const MAX_CHAR_LENGTH = 5000;

const truncateCss = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const optionCss = {
  display: "block",
  ...truncateCss,
};

export default React.memo(function AutoCompleteInput(props) {
  const {
    label = "",
    showAddOption = false,
    noteSaveType = "",
    onNoteTypeSaveChange = () => {},
    addOptionRenderer = () => <></>,
    onDialogClose = () => {},
    optionList = [],
    loading = false,
    value: defaultValue = null,
    updateList,
    onSelect,
    sx,
    ...rest
  } = props;
  const [list] = useState(optionList);
  const [value, setValue] = useState(defaultValue);
  const [open, toggleOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState({
    title: "",
  });

  useEffect(() => {
    // Setting initial value only
    let currentValue = !!value ? value : "";
    currentValue =
      typeof currentValue === "number" ? { id: currentValue } : currentValue;
    if (list.length && !isEmpty(currentValue)) {
      const isPresent = list.find((option) => option?.id === currentValue?.id);
      currentValue = isPresent ? isPresent : "";
    }
    setValue(currentValue);
  }, []);

  const handleClose = () => {
    setDialogValue({
      title: "",
    });
    toggleOpen(false);
    onDialogClose?.();
  };

  const handleSubmit = async (event) => {
    const userInput = dialogValue.title.trim();
    handleClose();
    onOptionChange(event, { title: userInput });
    const currentSelection = list.find((note) => note.title === userInput);
    onSelect?.(event, currentSelection);
    await updateList?.(dialogValue);
    event.stopPropagation();
  };

  const getFilterOptions = useCallback(
    (options, params) => {
      const filtered = filter(options, params) ?? [];
      if (showAddOption) {
        let isSame = !!filtered.length;
        if (!isSame) {
          const isFound =
            list.find((item) => item.title === params.inputValue.trim()) ??
            null;
          isSame = !!isFound;
        }
        if (params.inputValue !== "" && !isSame) {
          filtered.push({
            inputValue: params.inputValue,
            title: addOptionRenderer(params.inputValue),
          });
        }
      }
      return filtered;
    },
    [showAddOption, list, addOptionRenderer]
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === "string") {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.title;
  }, []);

  const onOptionChange = useCallback(
    (event, newValue, reason = "") => {
      if (reason === "clear") {
        onSelect?.(event, "");
      }
      if (typeof newValue === "string") {
        toggleOpen(true);
        setDialogValue({
          title: newValue,
        });
      } else if (newValue && newValue.inputValue) {
        toggleOpen(true);
        setDialogValue({
          title: newValue.inputValue,
        });
      } else {
        setValue(newValue);
        onSelect?.(event, newValue);
      }
    },
    [onSelect, setDialogValue, setValue, toggleOpen]
  );

  const optionRenderer = useCallback(
    (props, option) => (
      <li {...props} key={option?.id}>
        <span
          title={typeof option?.title === "string" ? option.title : ""}
          style={optionCss}
        >
          {option.title}
        </span>
      </li>
    ),
    []
  );

  const inputRenderer = useCallback(
    (params) => {
      return (
        <Box sx={{ position: "relative" }}>
          <TextField
            {...params}
            label={label}
            inputProps={{
              ...params?.inputProps,
              value:
                params.inputProps.value === "undefined"
                  ? ""
                  : params.inputProps.value,
              maxlength: MAX_CHAR_LENGTH,
            }}
            disabled={loading}
            autoFocus
          />
          {loading && (
            <Fade in>
              <LinearProgress
                sx={{ position: "absolute", bottom: 10, left: 1, right: 1 }}
              />
            </Fade>
          )}
        </Box>
      );
    },
    [loading, label]
  );

  const dialogInputChange = useCallback(
    (event) =>
      setDialogValue({
        title: event.target.value,
      }),
    []
  );

  const memoizedValue = useMemo(() => {
    if (!!value) {
      let currentValue = ["string", "number"].includes(typeof value)
        ? { id: value }
        : value;
      return currentValue;
    }
    return "";
  }, [value]);

  const getOptionEqualToValue = useCallback(
    (option) => {
      return (
        option?.id === memoizedValue?.id ||
        option?.title === memoizedValue?.title
      );
    },
    [memoizedValue]
  );

  return (
    <>
      <Autocomplete
        value={memoizedValue}
        onChange={onOptionChange}
        filterOptions={getFilterOptions}
        id="notes-autocomplete-dialog"
        options={list}
        getOptionLabel={getOptionLabel}
        renderOption={optionRenderer}
        sx={sx}
        renderInput={inputRenderer}
        isOptionEqualToValue={getOptionEqualToValue}
        disabled={loading}
        {...rest}
      />
      {dialogValue?.title && (
        <NotesDialog
          value={dialogValue.title}
          open={open}
          onClose={handleClose}
          handleSubmit={handleSubmit}
          dialogInputChange={dialogInputChange}
          noteSaveType={noteSaveType}
          onNoteTypeSaveChange={onNoteTypeSaveChange}
          list={list}
        />
      )}
    </>
  );
});
